import { NavLink, useNavigate } from "react-router-dom";
import { Sprout, LogOut, Settings as SettingsIcon, Archive, LayoutDashboard, KeyRound, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export function Navbar({ onOpenSettings, onOpenChangePassword }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <nav className="sticky top-0 z-40 w-full backdrop-blur-xl bg-[#F7F7F5]/85 border-b border-[#E5E5E0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-600 text-white">
            <Sprout size={18} strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-semibold text-stone-900">Hydro Stack</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Made by Sukesh Barua</div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <NavLink
            to="/"
            end
            data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all border ${
                isActive
                  ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                  : "bg-white text-stone-700 border-stone-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700"
              }`
            }
          >
            <LayoutDashboard size={14} strokeWidth={1.75} />
            Dashboard
          </NavLink>
          <NavLink
            to="/delivered"
            data-testid="nav-delivered"
            className={({ isActive }) =>
              `flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all border ${
                isActive
                  ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                  : "bg-white text-stone-700 border-stone-200 hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700"
              }`
            }
          >
            <Archive size={14} strokeWidth={1.75} />
            <span className="hidden xs:inline sm:inline">Completed</span>
            <span className="hidden sm:inline">&nbsp;Orders</span>
          </NavLink>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="open-settings-btn"
            onClick={onOpenSettings}
            className="gap-2 border-[#E5E5E0] bg-white hover:bg-stone-50"
          >
            <SettingsIcon size={15} strokeWidth={1.75} />
            <span className="hidden sm:inline">Settings</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                data-testid="user-menu-btn"
                className="gap-2 text-stone-600 hover:text-stone-900"
              >
                <User size={15} strokeWidth={1.75} />
                <span className="hidden sm:inline">{user?.username || "Account"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">
                <div className="text-stone-500">Signed in as</div>
                <div className="text-stone-900 font-mono mt-0.5 truncate">{user?.username}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onOpenChangePassword}
                data-testid="open-change-password-btn"
              >
                <KeyRound size={14} className="mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-rose-600 focus:text-rose-700"
              >
                <LogOut size={14} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
